"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { RichText } from "@sitecore-content-sdk/nextjs";
import { useMessages } from "next-intl";
import styles from "./VideoComponent.module.scss";

interface VideoPlayerProps {
  embedSrc: string;
  thumbnailUrl: string;
  thumbnailAlt: string;
  title: string;
  hiddenBlockStyles: string;
  rawUrlForCookieMessage: string;
  isPreviewMode: boolean;
}

const VideoPlayer = ({
  embedSrc,
  thumbnailUrl,
  thumbnailAlt,
  title,
  hiddenBlockStyles,
  rawUrlForCookieMessage,
  isPreviewMode,
}: VideoPlayerProps) => {
  const messages = useMessages();

  const [hasConsent, setHasConsent] = useState(false);
  const [isConsentCheckDone, setIsConsentCheckDone] = useState(false);
  const [isThumbnailClicked, setIsThumbnailClicked] = useState(false);

  useEffect(() => {
    const checkConsent = () => {
      if (isPreviewMode) {
        setHasConsent(true);
        setIsConsentCheckDone(true);
        return;
      }
      if (typeof window == "undefined" || !window.OptanonActiveGroups) return;

      const consent = window.OptanonActiveGroups.includes("C0007");
      // If consent was just granted, reset the thumbnail-clicked flag so the
      // user sees the thumbnail again rather than an iframe popping in.
      if (!hasConsent && consent) setIsThumbnailClicked(false);
      setHasConsent(consent);
      setIsConsentCheckDone((prev) => prev || true);
    };

    setTimeout(checkConsent);

    const handleConsentChange = () => checkConsent();
    window.addEventListener("OneTrustGroupsUpdated", handleConsentChange);

    return () => {
      window.removeEventListener("OneTrustGroupsUpdated", handleConsentChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cookieErrorTitle = messages["youtube-cookie-title"]
    ? messages["youtube-cookie-title"]
    : "YouTube cookie consent required";
  const cookieErrorDescription = messages["youtube-cookie-description"]
    ? messages["youtube-cookie-description"]
    : "Please accept marketing cookies to view this video.";
  const cookieErrorCTAText = messages["youtube-cookie-cta-text"]
    ? messages["youtube-cookie-cta-text"]
    : "Update Cookie Preferences";
  const cookieErrorBottomContent = (
    messages["youtube-cookie-bottom-content"]
      ? messages["youtube-cookie-bottom-content"]
      : ""
  )
    ?.replace("{##youtubeURL##}", rawUrlForCookieMessage)
    ?.replaceAll(String.raw`\"`, '"');

  const containerClass = [
    "ot-embed-container mb-5 relative w-full bg-current",
    styles.videoWrapper,
    "min-h-[100px] bg-no-repeat bg-cover bg-center overflow-hidden rounded-[6px]",
    !isConsentCheckDone || hasConsent ? "bg-current pb-[56.25%]" : "",
    !hasConsent && isConsentCheckDone
      ? `bg-black max-lg:!pb-0 lg:pb-[56.25%] ${styles.cookieScreen}`
      : "",
    hiddenBlockStyles,
  ]
    .filter(Boolean)
    .join(" ");

  const showIframe =
    hasConsent && ((thumbnailUrl && isThumbnailClicked) || !thumbnailUrl);

  return (
    <div
      className={containerClass}
      data-category="C0007"
      data-testid="video-component"
      data-role="video-component"
    >
      {isConsentCheckDone && (
        <div
          className={`lg:absolute lg:z-1 h-full flex w-full bg-black px-[15px] pt-[5px] pb-[10px] ${
            hasConsent ? "hidden" : ""
          }`}
          data-consent-message="C007"
        >
          <div className="flex flex-col justify-between flex-grow-1">
            <div>
              <h2 className="text-white text-[20px] mt-[15px] mb-[5px] font-medium leading-[1.1] pb-[5px]">
                {cookieErrorTitle}
              </h2>
              <div className="text-white text-base [&_p]:tracking-[0px]! richtext-content [&_p]:mb-[5px]!">
                <RichText field={{ value: cookieErrorDescription }} />
              </div>
            </div>
            <div className="flex justify-center min-h-[50px] items-center">
              <button
                type="button"
                id="ot-sdk-btn"
                className={`ot-sdk-show-settings onetrust-yt-btn overflow-visible normal-case font-[inherit] !text-ecolab-blue border-2 !border-ecolab-blue !text-[14px] !font-bold !rounded-[20px] !bg-white [&:hover]:underline [&:hover]:!text-cookie-btn-blue`}
              >
                {cookieErrorCTAText}
              </button>
            </div>
            <div className="text-white text-base richtext-content [&_p]:tracking-[0px]! [&_a]:text-white [&_a:hover]:!text-link-blue-dark-bg [&_a:focus]:!text-link-blue-dark-bg [&_a]:underline [&_p]:mb-[5px]!">
              <RichText field={{ value: cookieErrorBottomContent }} />
            </div>
          </div>
        </div>
      )}

      {hasConsent && thumbnailUrl && !isThumbnailClicked && (
        <button
          className={`absolute z-1 h-full w-full cover cursor-pointer ${styles.youtubeThumbnailWrapper}`}
          onClick={() => setIsThumbnailClicked(true)}
          tabIndex={0}
          aria-label={title}
          data-testid="thumbnail-image-wrapper"
        >
          {title && (
            <span
              className={`text-light-white text-left text-[18px] [&:hover]:text-white absolute top-0 left-0 right-0 z-3 ${styles.titleText}`}
            >
              {title}
            </span>
          )}
          <Image
            src={thumbnailUrl}
            width={800}
            height={600}
            alt={thumbnailAlt}
            className="object-cover w-full h-full"
            data-testid="thumbnail-image"
          />
        </button>
      )}

      {showIframe && (
        <iframe
          src={embedSrc}
          title={title}
          allowFullScreen
          className="absolute inset-0 z-[5] w-full h-full"
          aria-label={title}
          allow="autoplay"
          data-testid="video-component-iframe"
        />
      )}
    </div>
  );
};

export default VideoPlayer;
